import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react"
import { api, getToken, setToken, setUnauthorisedHandler } from "../api/client"
import { REFRESH_EVERY_MS, usePolling } from "../lib/usePolling"

/**
 * Session and workspace state.
 *
 * The signed-in user, the public config (countries, plans, OAuth providers),
 * the projects they can see, and the toast queue. `status` gates the whole
 * router: nothing renders until we know whether there is a session.
 */

const AppContext = createContext(null)

export function AppProvider({ children }) {
	const [status, setStatus] = useState("loading") // loading | ready | error
	const [user, setUser] = useState(null)
	const [config, setConfig] = useState(null)
	const [projects, setProjects] = useState([])
	const [people, setPeople] = useState([])
	const [pendingInvites, setPendingInvites] = useState([])
	const [error, setError] = useState(null)
	const [toasts, setToasts] = useState([])

	const toast = useCallback((message, tone = "info") => {
		const id = Math.random().toString(36).slice(2)
		setToasts((current) => [...current, { id, message, tone }])
		window.setTimeout(() => {
			setToasts((current) => current.filter((t) => t.id !== id))
		}, 6000)
	}, [])

	const dismissToast = useCallback((id) => {
		setToasts((current) => current.filter((t) => t.id !== id))
	}, [])

	const signOut = useCallback(() => {
		setToken(null)
		setUser(null)
		setProjects([])
		setPeople([])
		setPendingInvites([])
	}, [])

	// A rejected token anywhere in the app drops the session here, once.
	useEffect(() => {
		setUnauthorisedHandler(() => {
			setUser(null)
			setProjects([])
		})
	}, [])

	const refreshProjects = useCallback(async () => {
		const data = await api.projects()
		setProjects(data.projects)
		return data.projects
	}, [])

	// Keep the project list - and its open counts - current with what others
	// do, without a reload. Failures stay quiet: the next tick tries again.
	usePolling(() => refreshProjects().catch(() => {}), REFRESH_EVERY_MS, Boolean(user))

	const refreshPeople = useCallback(async () => {
		try {
			const data = await api.people()
			setPeople(data.people)
			return data.people
		} catch {
			return []
		}
	}, [])

	const refreshUser = useCallback(async () => {
		const data = await api.me()
		setUser(data.user)
		setPendingInvites(data.pending_invites || [])
		return data.user
	}, [])

	/** Called after a successful verify or OAuth callback. */
	const adoptSession = useCallback(
		async (token, nextUser) => {
			setToken(token)
			if (nextUser) setUser(nextUser)
			else await refreshUser()
			await Promise.all([refreshProjects(), refreshPeople()])
		},
		[refreshUser, refreshProjects, refreshPeople]
	)

	useEffect(() => {
		let cancelled = false

		;(async () => {
			try {
				const publicConfig = await api.config()
				if (cancelled) return
				setConfig(publicConfig)

				if (getToken()) {
					try {
						const data = await api.me()
						if (cancelled) return
						setUser(data.user)
						setPendingInvites(data.pending_invites || [])
						await Promise.all([refreshProjects(), refreshPeople()])
					} catch {
						// An expired or revoked token just means "signed out".
						setToken(null)
					}
				}
				if (!cancelled) setStatus("ready")
			} catch (cause) {
				if (cancelled) return
				setError(cause)
				setStatus("error")
			}
		})()

		return () => {
			cancelled = true
		}
	}, [refreshProjects, refreshPeople])

	const peopleById = useMemo(() => {
		const index = {}
		people.forEach((person) => {
			index[person.user_id] = person
		})
		if (user) index[user.user_id] = { ...user, ...index[user.user_id] }
		return index
	}, [people, user])

	const value = useMemo(
		() => ({
			status,
			error,
			config,
			user,
			isSignedIn: Boolean(user),
			timezone: user?.timezone || "UTC",
			adoptSession,
			refreshUser,
			signOut,
			projects,
			refreshProjects,
			people,
			peopleById,
			refreshPeople,
			pendingInvites,
			toast,
			toasts,
			dismissToast
		}),
		[
			status,
			error,
			config,
			user,
			adoptSession,
			refreshUser,
			signOut,
			projects,
			refreshProjects,
			people,
			peopleById,
			refreshPeople,
			pendingInvites,
			toast,
			toasts,
			dismissToast
		]
	)

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp() {
	const value = useContext(AppContext)
	if (!value) throw new Error("useApp must be used inside <AppProvider>")
	return value
}

/** Resolve a user id to something displayable, never to `undefined`. */
export function usePerson(userId) {
	const { peopleById } = useApp()
	return (
		peopleById[userId] || {
			user_id: userId,
			name: "Unknown",
			initials: "?",
			colour: "slate",
			role: "",
			company: ""
		}
	)
}
